import type { SubBasin } from "./types.ts";

/**
 * Color-codes sub-basins by whichever entity (zone, snow zone, outlet
 * point, aquifer) they're currently assigned to — used by both
 * SubBasinMap.tsx (polygon fill) and SubBasinCanvas.tsx (chip background)
 * while bulk-editing one of those in EditBar.tsx, so assignments read at a
 * glance in either view without opening each sub-basin's dialog. One
 * shared palette for every field: a color isn't tied to "always Zone 3",
 * it's just the Nth entity of whichever list is active right now — the
 * same map/cards/legend triplet works unchanged for zones, snow zones,
 * outlet points and aquifers.
 */
export type AssignmentColorField = "zoneCode" | "snowZoneCode" | "outletCode" | "aquiferCode";

export interface AssignmentColorMode {
  field: AssignmentColorField;
  entities: { code: number; name: string }[];
}

/**
 * Qualitative palette (Tableau10-derived), with the orange dropped — it's
 * already used for the selection highlight (SubBasinMap.tsx's
 * SELECTION_HIGHLIGHT/.canvas-chip-selected) and would read as "selected"
 * instead of "assigned to entity #2". Repeats past 9 entities — acceptable
 * past that many, the legend still lists every name correctly even if two
 * colors coincide. Exported (not just `buildColorMap` below) so
 * SubBasinMap.tsx's `<SnowPatterns>` can pre-render one diagonal-stripe
 * pattern per palette color, for sub-basins that have BOTH a snow zone and
 * an assignment color at once — see `patternKeyFor`.
 */
export const PALETTE = [
  "#4E79A7",
  "#E15759",
  "#59A14F",
  "#B07AA1",
  "#EDC948",
  "#76B7B2",
  "#FF9DA7",
  "#9C755F",
  "#86BCB6",
];

/** Fill for "not assigned to anything yet" — distinct from the palette above and from mapStyles.ts's PROBLEM_STYLE/UNLINKED_STYLE grays, which mean something else entirely. Light, so it takes dark text (see textColorFor). */
export const UNASSIGNED_COLOR = "#c7c2b8";

export function buildColorMap(entities: { code: number }[]): Map<number, string> {
  const map = new Map<number, string>();
  entities.forEach((e, i) => map.set(e.code, PALETTE[i % PALETTE.length]));
  return map;
}

/** `null` covers both sentinels a sub-basin's fields use for "unassigned" — `null` itself (zoneCode/snowZoneCode) and `0` (outletCode/aquiferCode, see projectEntities.ts). */
function assignedEntityCode(subBasin: SubBasin, field: AssignmentColorField): number | null {
  const raw = subBasin[field];
  return raw === 0 || raw == null ? null : raw;
}

export function colorFor(
  colorMap: Map<number, string>,
  field: AssignmentColorField,
  subBasin: SubBasin
): string {
  const code = assignedEntityCode(subBasin, field);
  if (code == null) return UNASSIGNED_COLOR;
  return colorMap.get(code) ?? UNASSIGNED_COLOR;
}

/** Every PALETTE entry is dark/saturated enough for white text; only the light neutral UNASSIGNED_COLOR needs dark text instead. */
export function textColorFor(fillColor: string): string {
  return fillColor === UNASSIGNED_COLOR ? "#3a352c" : "#ffffff";
}

/** Suffix for the `pattern-assign-{key}` id (SubBasinMap.tsx's `<SnowPatterns>`) matching a color returned by `colorFor` above — one of PALETTE's own indices, or "unassigned". Falls back to "unassigned" for any other color (shouldn't happen, `colorFor` only ever returns a PALETTE entry or UNASSIGNED_COLOR), rather than referencing a pattern id that doesn't exist. */
export function patternKeyFor(color: string): string {
  const index = PALETTE.indexOf(color);
  return index >= 0 ? String(index) : "unassigned";
}
